import React from 'react';

import { ConsoleShell } from '../../../design-system/components/shells';
import {
  Button,
  Drawer,
  PageHeader,
  Pagination,
  Select,
  Table,
  Toolbar,
} from '../../../design-system/components/desktop';
import {
  ActionRow,
  ConfirmDialog,
  ConsoleCell,
  ConsoleDenied,
  ConsoleEmpty,
  ConsoleError,
  ConsoleStack,
  DetailList,
  DetailRow,
  DetailSection,
  Notice,
  RejectReasonForm,
  ResolvedNote,
  StatusPill,
} from '../../../design-system/patterns/console';
import { formatCurrency } from '../../../utils/common';

/**
 * Payouts, the view. Pure.
 *
 * WHAT DIED HERE
 * --------------
 * The third pagination implementation (`components/PaginationControls.jsx`, a
 * bare Previous/Next pair), the second `StatusBadge` (`components/StatusBadge.jsx`,
 * which title-cased the raw value and emitted `admin-status-<value>`, so it had
 * no idea "in_transit" and "delivered" are different severities), a hand-rolled
 * `<table>`, a hand-rolled `TableSkeleton`, a hand-rolled `ErrorMessage`, and
 * `PayoutDetailPanel`, a fixed-position div with no focus trap, no Escape and
 * no scroll lock, opened by clicking a row.
 *
 * The gradient Confirm button and the outlined Cancel in the approve dialog are
 * both `Button` now, ink and ghost.
 *
 * PAYOUT AND BANK DATA IS SENSITIVE
 * ---------------------------------
 * The account number is shown in full because the administrator paying the
 * seller needs it, which is what this screen is for. What must not happen is it
 * leaking anywhere else: nothing on this page is logged, nothing is put in a
 * URL, and every fixture in the state registry uses an obviously masked value.
 *
 * "MISSING BANK DETAILS" IS A REAL STATE and it is a `Notice`, not a filled red
 * panel: the sentence is what carries it. The old page only showed the warning
 * once a payout had already been approved, which is exactly one step too late,
 * so it now shows on any payout awaiting a decision as well.
 *
 * ONE OVERLAY AT A TIME
 * ---------------------
 * Opening the confirmation or the reason form CLOSES the drawer, and cancelling
 * either one brings it back. That is the upstream template's rule verbatim:
 * "the drawer steps aside so the reason form is the only thing asking for a
 * decision". It is not only a taste call. `Drawer` and `Modal` share
 * `--ds-z-modal` and both install a focus trap through `useModalLayer`, so two
 * open at once means two traps fighting over Tab and an Escape that closes both.
 * `open` is derived, not a second piece of state, so the two cannot disagree.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Requested' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
];

export function AdminPayoutsView({
  payouts = [],
  loading = false,
  error,
  onRetry,
  denied = false,

  status = '',
  onStatusChange,

  page = 1,
  pageCount = 1,
  pageSize = 10,
  total = 0,
  onPageChange,

  selected,
  onOpen,
  onClose,

  onApprove,
  onReject,
  actionBusy = false,
  confirmApproveOpen = false,
  onConfirmApproveOpen,
  onConfirmApproveClose,
  rejectOpen = false,
  onRejectOpen,
  onRejectClose,
}) {
  const columns = [
    {
      key: 'store_name',
      header: 'Seller',
      render: (row) => (
        <ConsoleCell
          title={row.store_name || 'No store name'}
          sub={row.seller_email}
          initials={(row.store_name || 'S').slice(0, 2)}
        />
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      render: (row) => formatCurrency(row.amount),
    },
    { key: 'status', header: 'Status', render: (row) => <StatusPill status={row.status} /> },
    {
      key: 'created_at',
      header: 'Requested',
      muted: true,
      render: (row) =>
        row.created_at ? new Date(row.created_at).toLocaleDateString('en-ZA') : 'Unknown',
    },
  ];

  const shell = (children) => (
    <ConsoleShell
      variant="admin"
      title="Payouts"
      breadcrumbs={[{ label: 'Operations' }, { label: 'Payouts' }]}
      showSearch={false}
    >
      {children}
    </ConsoleShell>
  );

  if (denied) return shell(<ConsoleDenied />);

  const currentStatus = String(selected?.status || '').toLowerCase();
  const isPending = currentStatus === 'pending';
  const bank = selected?.bank_details;
  const missingBank = Boolean(selected) && !bank && currentStatus !== 'rejected';

  return shell(
    <>
      <ConsoleStack>
        <PageHeader
          title="Payouts"
          sub={
            loading
              ? 'Loading payout requests'
              : `${payouts.length} shown of ${total} ${total === 1 ? 'request' : 'requests'}`
          }
        />

        <Table
          columns={columns}
          data={error ? [] : payouts}
          rowKey="id"
          loading={loading}
          onRowClick={(row) => onOpen(row)}
          emptyState={
            error ? (
              <ConsoleError error={error} onRetry={onRetry} title="Could not load payouts" />
            ) : (
              <ConsoleEmpty
                filtered={Boolean(status)}
                illustration="no-payouts-yet"
                title="No payout requests"
                text="Requests appear here when a seller asks to be paid out."
                filteredText="No payout request has that status. Clear the status filter to see every request again."
                onClear={() => onStatusChange && onStatusChange('')}
              />
            )
          }
          rowActions={(row) => (
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                onOpen(row);
              }}
            >
              Review
            </Button>
          )}
          toolbar={
            <Toolbar
              filters={
                <Select
                  options={STATUS_OPTIONS}
                  value={status}
                  onChange={onStatusChange}
                  label="Status"
                  placeholder="All statuses"
                />
              }
            />
          }
          pagination={
            pageCount > 1 ? (
              <Pagination
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                totalItems={total}
                itemNoun="requests"
                onPageChange={onPageChange}
              />
            ) : null
          }
        />
      </ConsoleStack>

      <Drawer
        open={Boolean(selected) && !confirmApproveOpen && !rejectOpen}
        onClose={onClose}
        kicker="Payout request"
        title={selected?.store_name || 'Payout request'}
        footer={
          isPending ? (
            <ActionRow>
              <Button variant="ghost" onClick={onRejectOpen} disabled={actionBusy}>
                Reject payout
              </Button>
              <Button variant="primary" onClick={onConfirmApproveOpen} disabled={actionBusy}>
                Approve payout
              </Button>
            </ActionRow>
          ) : (
            <ResolvedNote>
              {currentStatus === 'approved'
                ? 'This payout has been approved and is waiting to be paid.'
                : currentStatus === 'paid'
                  ? 'This payout has been marked as paid.'
                  : currentStatus === 'rejected'
                    ? 'This payout was rejected.'
                    : 'No decision is pending on this request.'}
            </ResolvedNote>
          )
        }
      >
        {selected ? (
          <>
            <DetailSection>
              <DetailList>
                <DetailRow label="Amount" value={formatCurrency(selected.amount)} />
                <DetailRow label="Status" value={<StatusPill status={selected.status} />} keepEmpty />
                <DetailRow label="Seller email" value={selected.seller_email} />
                <DetailRow label="Payout reference" value={selected.id} />
                <DetailRow label="Seller reference" value={selected.seller_id} />
                <DetailRow
                  label="Requested"
                  value={
                    selected.created_at
                      ? new Date(selected.created_at).toLocaleString('en-ZA')
                      : undefined
                  }
                />
              </DetailList>
            </DetailSection>

            {bank ? (
              <DetailSection title="Bank account">
                <DetailList>
                  <DetailRow label="Account holder" value={bank.account_holder_name} />
                  <DetailRow label="Bank" value={bank.bank_name} />
                  <DetailRow label="Branch" value={bank.branch_name} />
                  <DetailRow label="Account number" value={bank.account_number} />
                  <DetailRow label="IBAN" value={bank.ibn_number} />
                </DetailList>
              </DetailSection>
            ) : null}

            {missingBank ? (
              <DetailSection title="Bank account">
                <Notice title="No bank details on file">
                  This seller has not supplied an account, so this payout cannot be paid. Ask them
                  to add their bank details before approving.
                </Notice>
              </DetailSection>
            ) : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirmApproveOpen}
        onClose={onConfirmApproveClose}
        onConfirm={onApprove}
        title="Approve payout"
        confirmLabel="Approve payout"
        busy={actionBusy}
        busyLabel="Processing"
      >
        <p>
          <strong>{formatCurrency(selected?.amount)}</strong> will be approved for payout to{' '}
          <strong>{selected?.store_name}</strong>.
        </p>
      </ConfirmDialog>

      <RejectReasonForm
        open={rejectOpen}
        onClose={onRejectClose}
        onSubmit={onReject}
        title="Reject payout"
        submitLabel="Reject payout"
        busy={actionBusy}
        hint="The seller sees this. Say why the request was refused."
      />
    </>,
  );
}

export default AdminPayoutsView;
